import { useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'

type AddAbbreviationRowProps = {
	onAdd: (abbreviation: string, expansion: string) => void
	existingAbbreviations: string[]
	count: number
}

const MAX_ABBREVIATIONS = 100

export default function AddAbbreviationRow({
	onAdd,
	existingAbbreviations,
	count,
}: AddAbbreviationRowProps) {
	const [abbreviation, setAbbreviation] = useState('')
	const [expansion, setExpansion] = useState('')
	const [error, setError] = useState<string | null>(null)
	const abbreviationRef = useRef<HTMLInputElement>(null)
	const atLimit = count >= MAX_ABBREVIATIONS

	const abbr = abbreviation.trim()

	const validate = (): string | null => {
		const exp = expansion.trim()

		if (abbr.length < 2) return 'Abbreviation must be at least 2 characters'
		if (abbr.length > 50) return 'Abbreviation must be 50 characters or less'
		if (/[^a-zA-Z]/.test(abbr)) return 'Abbreviation can only contain letters (a-z, A-Z)'
		if (existingAbbreviations.includes(abbr)) return 'You already have an abbreviation with this name'
		if (!exp) return 'Expansion text is required'
		if (exp.length > 500) return 'Expansion must be 500 characters or less'

		return null
	}

	const handleSubmit = () => {
		const validationError = validate()
		if (validationError) {
			setError(validationError)
			return
		}

		onAdd(abbreviation.trim(), expansion.trim())
		setAbbreviation('')
		setExpansion('')
		setError(null)
		abbreviationRef.current?.focus()
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			handleSubmit()
		}
	}

	if (atLimit) {
		return (
			<div className="rounded-md border border-border/50 bg-muted/30 px-4 py-3">
				<p className="text-sm text-muted-foreground">
					Maximum 100 abbreviations reached. Delete one to add another.
				</p>
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center gap-2">
				<Input
					ref={abbreviationRef}
					value={abbreviation}
					onChange={(e) => {
						setAbbreviation(e.target.value.replace(/[^a-zA-Z]/g, ''))
						setError(null)
					}}
					onKeyDown={handleKeyDown}
					placeholder="abbreviation"
					maxLength={50}
					className="w-32 font-mono text-sm"
					autoComplete="off"
				/>
				<Input
					value={expansion}
					onChange={(e) => {
						setExpansion(e.target.value)
						setError(null)
					}}
					onKeyDown={handleKeyDown}
					placeholder="expands to..."
					maxLength={500}
					className="flex-1 text-sm"
					autoComplete="off"
				/>
				<Button
					variant="outline"
					size="sm"
					onClick={handleSubmit}
					disabled={!abbreviation.trim() || !expansion.trim()}
					className="shrink-0 gap-1"
				>
					<Plus className="h-3.5 w-3.5" />
					Add
				</Button>
			</div>
			{error && (
				<p className="text-xs text-destructive pl-1">{error}</p>
			)}
		</div>
	)
}
